import { Directive, ElementRef, HostListener, OnInit, AfterViewInit } from '@angular/core';
import { NgControl, ValidationErrors, ControlValueAccessor } from '@angular/forms';

@Directive({
  selector: '[phoneFormat]',
  standalone: true
})
export class PhoneFormatDirective implements OnInit, AfterViewInit, ControlValueAccessor {
  private lastFormattedValue: string = '';
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private isDataBinding: boolean = false;

  constructor(
    private el: ElementRef,
    private control: NgControl
  ) {
    if (this.control) {
      this.control.valueAccessor = this;
      
      // Add our validator to the control's validator list
      const existingValidators = this.control.control?.validator;
      this.control.control?.setValidators((control) => {
        const existingErrors = existingValidators ? existingValidators(control) : null;
        const ourErrors = this.validatePhoneNumber(control?.value);
        return existingErrors || ourErrors;
      });
    }
  }

  // ControlValueAccessor methods
  writeValue(value: any): void {
    console.log('writeValue called with:', value);
    
    // Set flag to indicate data binding is in progress
    this.isDataBinding = true;
    
    if (value !== undefined && value !== null && value !== '') {
      const formatted = this.formatPhoneNumber(value.toString());
      this.lastFormattedValue = formatted;
      this.el.nativeElement.value = formatted;
    } else {
      // Don't clear the field if it's just an empty string from initialization
      // Only clear if it's explicitly null/undefined
      if (value === null || value === undefined) {
        this.el.nativeElement.value = '';
      }
    }
    
    // Reset flag after a short delay
    setTimeout(() => {
      this.isDataBinding = false;
    }, 100);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }

  private formatValue(value: any) {
    console.log('formatValue', value);
    if (value) {
      const strValue = value.toString();
      const formatted = this.formatPhoneNumber(strValue);
      
      // Only update if the formatted value is different
      if (formatted !== this.lastFormattedValue) {
        this.lastFormattedValue = formatted;
        this.el.nativeElement.value = formatted;
        
        // Store raw value
        const numericValue = strValue.replace(/\D/g, '');
        this.onChange(numericValue);
      }
    }
  }

  ngOnInit() {
    // Initial format attempt
    if (this.control?.value) {
      this.writeValue(this.control.value);
    }
  }

  ngAfterViewInit() {
    // Second format attempt after view init
    setTimeout(() => {
      if (this.control?.value) {
        this.writeValue(this.control.value);
      }
    });
  }

  private validatePhoneNumber(value: string | null): ValidationErrors | null {
    if (!value) return null;
    
    const numbers = value.toString().replace(/\D/g, '');
    
    // Check if all digits are the same
    if (numbers.length === 10) {
      const firstDigit = numbers[0];
      const allSameDigits = numbers.split('').every(digit => digit === firstDigit);
      if (allSameDigits) {
        return { sameDigits: true };
      }
    }
    
    return null;
  }

  private formatPhoneNumber(value: string): string {
    const numbers = value.replace(/\D/g, '').substring(0, 10);
    let formatted = '';
    
    if (numbers.length > 0) {
      formatted = '(' + numbers.substring(0, 3);
      if (numbers.length > 3) {
        formatted += ') ' + numbers.substring(3, 6);
        if (numbers.length > 6) {
          formatted += '-' + numbers.substring(6);
        }
      }
    }
    return formatted;
  }

  private getCursorPosition(oldValue: string, newValue: string, currentCursor: number, key: string): number {
    // Get raw numbers before cursor
    const numbersBeforeCursor = oldValue.substring(0, currentCursor).replace(/\D/g, '').length;
    const isBackspace = key === 'Backspace';
    const isDelete = key === 'Delete';

    // Handle backspace/delete at format boundaries
    if (isBackspace || isDelete) {
      
     
      if (currentCursor <= 1) return 0;
      // Special positions for backspace
      if (isBackspace) {
        if(currentCursor < 4) return currentCursor;
        if(currentCursor < 9) return currentCursor+1;
        return currentCursor+2;
      }
      console.log('currentCursor', currentCursor);
      return currentCursor;
    }

    // Get the number of digits in the input
    const totalDigits = newValue.replace(/\D/g, '').length;

    // If typing a new digit, calculate the next cursor position
    if (!isBackspace && !isDelete && /^\d$/.test(key)) {
      if (totalDigits <= 3) {
        return totalDigits + 1; // Account for opening parenthesis
      } else if (totalDigits <= 6) {
        return totalDigits + 3; // Account for parentheses and space
      } else if (totalDigits <= 10) {
        return totalDigits + 4; // Account for all formatting characters
      }
    }
    // For cursor movement without typing
    return currentCursor;
  }



  @HostListener('keyup', ['$event'])
  onKeyUp(event: KeyboardEvent) {
    // Skip processing if data binding is in progress
    if (this.isDataBinding) {
      return;
    }
    
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab'].includes(event.key)) {
      const input = this.el.nativeElement;
      const originalValue = input.value;
      const cursorPos = input.selectionStart || 0;
      
      // Get the raw number value
      const rawValue = originalValue.replace(/\D/g, '');
      
      // Format the number
      const formatted = this.formatPhoneNumber(rawValue);
      
      // Update the input value
      input.value = formatted;
      
      // Calculate the new cursor position
      const newCursorPos = this.getCursorPosition(
        originalValue, 
        formatted, 
        cursorPos,
        event.key
      );

      // Set the cursor position
      setTimeout(() => {
        input.setSelectionRange(newCursorPos, newCursorPos);
      }, 1);
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: InputEvent) {
    // Skip processing if data binding is in progress
    if (this.isDataBinding) {
      return;
    }
    
    const input = this.el.nativeElement;
    const value = input.value;
    const cursorPos = input.selectionStart || 0;
    
    // If this is a paste operation, format immediately
    if (event.inputType === 'insertFromPaste') {
      const formatted = this.formatPhoneNumber(value);
      this.lastFormattedValue = formatted;
      input.value = formatted;
      // Place cursor at the end for paste operations
      input.setSelectionRange(formatted.length, formatted.length);
    }
    
    // Store raw value and notify of changes
    const numericValue = value.replace(/\D/g, '');
    this.onChange(numericValue);
    this.onTouched();
    
    if (this.control.control) {
      this.control.control.updateValueAndValidity();
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace',
      'Tab',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'v',  // Allow Ctrl+V for paste
      'V'   // Allow Ctrl+V for paste (Shift held)
    ];

    // Allow Ctrl+V for paste
    if ((event.ctrlKey || event.metaKey) && (event.key === 'v' || event.key === 'V')) {
      return;
    }

    // Allow numeric keys (0-9) and special keys
    if (
      !allowedKeys.includes(event.key) &&
      !(event.key >= '0' && event.key <= '9')
    ) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    // Don't prevent default - let the native paste happen
    const input = this.el.nativeElement;
    setTimeout(() => {
      const value = input.value;
      const formatted = this.formatPhoneNumber(value);
      input.value = formatted;
      
      // Store raw value in form control
      const numericValue = value.replace(/\D/g, '').substring(0, 10);
      if (this.control.control) {
        this.control.control.setValue(numericValue, { emitEvent: false });
      }
      
      // Place cursor at the end for paste operations
      input.setSelectionRange(formatted.length, formatted.length);
    }, 0);
  }

  @HostListener('blur')
  onBlur() {
    const input = this.el.nativeElement;
    const value = input.value.replace(/\D/g, '');
    const formatted = this.formatPhoneNumber(value);
    
    this.lastFormattedValue = formatted;
    input.value = formatted;
    
    // Notify of changes and touched state
    this.onChange(value);
    this.onTouched();
  }
} 